import { SensorType } from '../../types/sensor-type';
import { BaseSensorWebView } from '../../base/view/BaseSensorWebView';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import _ from 'lodash';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../editIntegration/EditIntegrationConstants';
import { WEB_COMPONENT_GZ_LABELED_CONTENT, WEB_COMPONENT_GZ_TOOLTIP } from '../../../../webcomponents.constants';
import { htmlEncode } from '../../../../utils/escape.utils';
import { Sensor } from '../../../common/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzInputFileWebComponent } from '../../../../ui/gzInputFile/index';
import { FontSizes } from '../../../../types/common/types';

export class GCPSensorView extends BaseSensorWebView {
  public formGroup: FormGroup;

  public dom: {
    $subscriptionId: GzFormFieldWebComponent;
    $importFileLabel: any;
    $aboveImportFile: any;
    $buttonImportFile: GzButtonWebComponent;
    $importFileWrapper: GzFormFieldWebComponent;
    $importFile: GzInputFileWebComponent;
  };

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.GCP}">
        <gz-title fontsize="${
          FontSizes.HEADING_6
        }" class="important-sensor-info mb-2 ds-icon-notification_warning">${dependencies.translateFn(
      'IMPORTANT: Enabling Pub/Sub subscriptions may incur additional costs. These services are required for a successful integration.'
    )}
        </gz-title>

          <gz-title class="sensor-title">${dependencies.translateFn(
            'Before you integrate Google Cloud Platform with GravityZone, make sure the following requirements are met:'
          )}
          </gz-title>
          <ol>
                <li>
                  ${dependencies.translateFn(
                    'Have a project and an Administrator account set up in the Google Cloud console'
                  )}
                </li>
                <li>
                  ${dependencies.translateFn(
                    'Have a Pub/Sub topic set up with a default subscription and a Log router associated with the topic'
                  )}
                </li>
                <li>
                  ${dependencies.translateFn('Have the Pub/Sub topic configured with the Pub/Sub Publisher role')}
                </li>
                <li>
                  ${dependencies.translateFn('Have an IAM service account configured with the Pub/Sub Subscriber role')}
                </li>
                <li>
                  ${dependencies.translateFn('Have the service account key exported in JSON format')}
                </li>
                <li>
                  ${dependencies.translateFn(
                    'Optionally, for incident response capabilities, have the following scope set up in the Manage domain-wide delegation section: https://mail.google.com '
                  )}
                </li>
          </ol>
          <p>${dependencies.translateFn(
            'For a complete Google Cloud Platform sensor integration guide, refer to {detailLink}.',
            {
              detailLink:
                '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452350-the-google-cloud-platform-sensor-integration.html"><span>' +
                dependencies.translateFn('this KB article') +
                '</span></gz-link>',
            }
          )}</p>
        </div>
        `;
  }

  public static getSensorSpecifics(sensor: Sensor, dependencies: any) {
    let result = ``;
    if (sensor.specifics.subscriptionId) {
      result += GCPSensorView.buildSensorDetailInfo(sensor.specifics.subscriptionId, dependencies);
    }

    return result;
  }

  private static buildSensorDetailInfo(value: string, dependencies: any): string {
    const label = dependencies.translateFn('Topic subscription ID');
    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${label}" hasColon="true">
                <${WEB_COMPONENT_GZ_TOOLTIP} class="tooltip-subscriptionId" tooltipText="${htmlEncode(value)}">
                    ${value}
                </${WEB_COMPONENT_GZ_TOOLTIP}>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  protected createFormGroup() {
    this.dom.$importFileWrapper.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$subscriptionId.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.SubscriptionId.MaxLength,
        this.dependencies.translateFn('The Subscription ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.SubscriptionId.MaxLength,
        })
      ),
    ];

    this.formGroup = new FormGroup({
      subscription_id: this.dom.$subscriptionId,
      key_json: this.dom.$importFileWrapper,
    });
  }

  getDomElements(): void {
    super.getDomElements();
    this.dom = {
      $subscriptionId: this.shadow.querySelector('#subscription_id'),
      $importFileLabel: this.shadow.querySelector('#label_for_gcp_file'),
      $aboveImportFile: this.shadow.querySelector('#label_for_gcp_file_above'),
      $buttonImportFile: this.shadow.querySelector('#gcp_btn_import_file'),
      $importFileWrapper: this.shadow.querySelector('#gcp_import_file_wrapper'),
      $importFile: this.shadow.querySelector('#gcp_import_file'),
      ...this.baseDom,
    };
    this.getTranslations();
  }

  setTranslatedStrings(): void {
    this.dom.$subscriptionId.label = this.dependencies.translateFn('Topic subscription ID');
    this.dom.$importFileLabel.innerText = this.dependencies.translateFn(
      'Import the JSON file with your service account details.'
    );
    this.dom.$buttonImportFile.innerText = this.dependencies.translateFn('Import file');
    this.dom.$aboveImportFile.innerText = this.dependencies.translateFn('Service account details') + ' *';
    this.setBaseTranslatedStrings(SensorType.GCP);
  }

  protected async setViewData(viewData) {
    super.setViewData(viewData);
    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        subscription_id: _.get(viewData, ['response', 'configuration', 'subscription_id'], ''),
      };
    }
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.baseDom.$sensorSelectWrapper.disabled = !isEnabled;
    if (isEnabled) {
      this.formGroup.setChildrenAttribute('disabled', '');
    } else {
      this.formGroup.setChildrenAttribute('disabled', 'disable');
    }
  }
}
